import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Banner } from 'components/banner';
import Persist, { StorageKind } from 'lib/persist';
import { PromoEngineBanner, PromoEngineResponseApi } from 'lib/promoEngine/types';
import { getAlphaFromHex } from 'utility/functions';

const localStorage = Persist(StorageKind.local);

const mapBannerColor = (banner: PromoEngineBanner): Banner => {
  const backgroundColor = {
    hex: banner.backgroundColor,
    alpha: getAlphaFromHex(banner.backgroundColor),
  };
  return { ...banner, backgroundColor };
};

const mapBanner = (bannerList: Array<PromoEngineBanner>) => {
  return bannerList.map((banner) => mapBannerColor(banner));
};

// Define our single API slice object
export const promoEngineApiSlice = createApi({
  // The cache reducer expects to be added at `state.api` (already default - this is optional)
  reducerPath: 'api_promo_engine',
  // All of our requests will have URLs starting with '/fakeApi'
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.NEXT_PUBLIC_PROMO_ENGINE}`,
    credentials: 'same-origin',
    prepareHeaders: (headers) => {
      const accessToken = localStorage.getItem('token');
      if (accessToken) {
        headers.set('authorization', `Bearer ${accessToken}`);
        headers.set('Content-Type', 'application/json');
      }
      return headers;
    },
  }),
  // The "endpoints" represent operations and requests for this server
  endpoints: (builder) => ({
    // The `getPosts` endpoint is a "query" operation that returns data
    getPageBanners: builder.query<PromoEngineResponseApi | undefined, string>({
      query: (id) => `/pages/${id}`,
      transformResponse: (response: PromoEngineResponseApi<PromoEngineBanner>) => {
        const {
          heroBannerList: _heroBannerList,
          pageBannerList: _pageBannerList,
          portraitBanner: _portraitBanner,
        } = response ?? {};

        const heroBannerList = mapBanner(_heroBannerList);
        const pageBannerList = mapBanner(_pageBannerList);
        const portraitBanner = mapBannerColor(_portraitBanner ?? {});

        return { heroBannerList, portraitBanner, pageBannerList };
      },
    }),
  }),
});

// Export the auto-generated hook for the `getPosts` query endpoint
export const { useGetPageBannersQuery, useLazyGetPageBannersQuery } = promoEngineApiSlice;
